import type {
  SessionEvent,
  ConversationModel,
  ConversationItem,
  Turn,
  TurnItem,
  ToolCallItem,
  SubagentGroup,
  SubagentGroupItem,
  AssistantTextItem,
  UserMessageItem,
  RemoteSteerableItem,
  AbortItem,
  AskUserItem,
  PermissionItem,
  ElicitationItem,
  PlanApprovalItem,
  PairedToolCall,
  UserMessageEvent,
  AssistantTurnStartEvent,
  AssistantMessageEvent,
  AssistantIntentEvent,
  ToolExecutionStartEvent,
  ToolExecutionCompleteEvent,
  AbortEvent,
  SubagentStartedLikeEvent,
  SubagentCompletedLikeEvent,
  SubagentFailedLikeEvent,
  SessionStartEvent,
  SessionModelChangeEvent,
  SessionRemoteSteerableChangedEvent,
  PermissionRequestedEvent,
  PermissionCompletedEvent,
  ElicitationRequestedEvent,
  ElicitationCompletedEvent,
  ExitPlanModeRequestedEvent,
  ExitPlanModeCompletedEvent,
  UserInputRequestedEvent,
  UserInputCompletedEvent,
} from '../types/events'
import {isTurn} from '../types/events'
import type {NormalizedPrompt, ExitPlanModeArguments} from '@github-ui/copilot-shared-ui/ahp/interactive-prompt-types'
import {normalizeAskUserArguments} from '../components/session-logs/prompt-log-utils'
import type {Command, RunSetupArgs} from '../types/session'
import {isTurnItemVisible, isUserMessageVisible} from './events/visibility'
import {commandDisplayContent, isInjectedSource} from './command-helpers'
import {normalizePermissionArguments} from './permission-prompt-helpers'

type PromptItem = AskUserItem | PermissionItem | ElicitationItem | PlanApprovalItem

type PromptKind = 'ask_user' | 'permission' | 'elicitation' | 'plan_approval'

const PROMPT_KINDS = new Set<PromptKind>(['ask_user', 'permission', 'elicitation', 'plan_approval'])

function isPromptItem(item: TurnItem): item is PromptItem {
  return PROMPT_KINDS.has(item.kind as PromptKind)
}

function getPromptKey(item: PromptItem): string {
  if (item.kind === 'plan_approval') {
    return item.toolCallId ?? item.requestId
  }

  return item.toolCallId
}

function getToolTitle(data: unknown): string | undefined {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return undefined
  const record = data as Record<string, unknown>
  const toolTitle = record.toolTitle ?? record.displayName
  return typeof toolTitle === 'string' ? toolTitle : undefined
}

/**
 * Extracts MCP tool name → display name mappings from run_setup tool calls
 * that have mcp_setup details with toolNamesToDisplayNames.
 */
function extractMcpMappingsFromItems(items: ConversationItem[]): Record<string, string> {
  const mappings: Record<string, string> = {}

  function walkItems(turnItems: TurnItem[]): void {
    for (const item of turnItems) {
      if (item.kind === 'tool_call' && item.toolCall.toolName === 'run_setup') {
        const args = item.toolCall.arguments as RunSetupArgs | undefined
        if (args?.details?.type === 'mcp_setup' && args.details.toolNamesToDisplayNames) {
          Object.assign(mappings, args.details.toolNamesToDisplayNames)
        }
      } else if (item.kind === 'subagent_group') {
        for (const turn of item.group.turns) {
          walkItems(turn.items)
        }
      }
    }
  }

  for (const conversationItem of items) {
    if (isTurn(conversationItem)) {
      walkItems(conversationItem.items)
    }
  }

  return mappings
}

function promptItemToNormalized(item: PromptItem): NormalizedPrompt {
  switch (item.kind) {
    case 'ask_user':
      return {
        toolCallId: item.toolCallId,
        toolName: 'ask_user',
        arguments: normalizeAskUserArguments({
          question: item.question,
          choices: item.choices ?? undefined,
          allowFreeform: item.allowFreeform,
        }),
        createdAt: item.timestamp,
      }
    case 'permission': {
      const eventData = item.requestEvent.data as unknown as Record<string, unknown>
      const permReq = eventData.permissionRequest as Record<string, unknown>
      const promptReq = eventData.promptRequest as Record<string, unknown> | undefined
      return {
        toolCallId: item.toolCallId,
        toolName: 'permission_request',
        arguments: normalizePermissionArguments(permReq, promptReq),
        createdAt: item.timestamp,
      }
    }
    case 'elicitation':
      return {
        toolCallId: item.toolCallId,
        toolName: 'ask_user',
        arguments: normalizeAskUserArguments({
          message: item.message,
          requestedSchema: item.schema,
        }),
        createdAt: item.timestamp,
      }
    case 'plan_approval':
      return {
        toolCallId: item.toolCallId ?? item.requestId,
        toolName: 'exit_plan_mode',
        arguments: {
          summary: item.summary,
          planContent: item.planContent ?? undefined,
          actions: item.actions ?? undefined,
          recommendedAction: item.recommendedAction ?? undefined,
        } satisfies ExitPlanModeArguments,
        createdAt: item.timestamp,
      }
  }
}

interface ProcessingState {
  items: ConversationItem[]
  currentTurn: Turn | null
  toolCallIndex: Map<string, ToolCallItem>
  // Records the turn items array that owns each tool call so later hierarchy events can move it into a subagent.
  toolCallContainers: Map<string, TurnItem[]>
  // Maps requestId/toolCallId → prompt item for completion pairing
  promptIndex: Map<string, PromptItem>
  // Maps custom_agent.started toolCallId → SubagentGroup
  activeSubagents: Map<string, SubagentGroupItem>
  // Maps user message source and subagent → Turn for dedup (pending→real transitions)
  userMessagesBySource: Map<string, Map<string | null, Turn>>
  currentIntent: string | null
  isWorking: boolean
  sessionModel: string | null
}

function createTurn(turnId: string | null): Turn {
  return {
    turnId,
    userMessage: null,
    items: [],
    intent: null,
    isComplete: false,
  }
}

function createUserMessageItem(event: UserMessageEvent): UserMessageItem {
  return {
    kind: 'user_message',
    id: event.id,
    timestamp: event.timestamp,
    content: event.data.content,
    source: event.data.source,
    isPending: event.pending === true,
    isDismissed: event.dismissed === true,
  }
}

function ensureCurrentTurn(state: ProcessingState): Turn {
  if (!state.currentTurn) {
    state.currentTurn = createTurn(null)
    state.items.push(state.currentTurn)
  }

  return state.currentTurn
}

function getEventSubagentIds(event: SessionEvent): string[] {
  const agentId = (event as {agentId?: string}).agentId
  const parentToolCallId = (event as {data?: {parentToolCallId?: string}}).data?.parentToolCallId
  return [...new Set([parentToolCallId, agentId].filter((id): id is string => !!id))]
}

function resolveEventSubagentId(state: ProcessingState, event: SessionEvent): string | null {
  for (const id of getEventSubagentIds(event)) {
    const subagentItem = state.activeSubagents.get(id)
    if (subagentItem) {
      return subagentItem.group.agentId
    }
  }
  return null
}

/**
 * Extracts metadata from a `task` tool call's arguments: the short `description`
 * (e.g. "Explore repo structure") and the full `prompt` (subagent instructions).
 */
function getTaskMetadata(item: ToolCallItem | undefined): {description: string; prompt: string} {
  if (!item) return {description: '', prompt: ''}
  let args: unknown = item.toolCall.arguments
  if (typeof args === 'string') {
    try {
      args = JSON.parse(args)
    } catch {
      return {description: '', prompt: ''}
    }
  }

  if (!args || typeof args !== 'object') return {description: '', prompt: ''}
  const record = args as Record<string, unknown>
  const description = typeof record.description === 'string' ? record.description : ''
  const prompt = typeof record.prompt === 'string' ? record.prompt : ''
  return {description, prompt}
}

/**
 * Resolves where an event's items should be appended. Sub-agent events are
 * routed into their group's turns when a group exists for their key.
 */
function resolveTargetTurnItems(state: ProcessingState, event?: SessionEvent): TurnItem[] {
  const subagentId = event ? resolveEventSubagentId(state, event) : null
  if (subagentId) {
    const subagentItem = state.activeSubagents.get(subagentId)
    if (subagentItem) {
      const group = subagentItem.group
      if (group.turns.length === 0) {
        group.turns.push(createTurn(null))
      }

      const lastTurn = group.turns[group.turns.length - 1]
      if (!lastTurn) {
        throw new Error('Subagent group has no turns after ensuring one exists')
      }

      return lastTurn.items
    }
  }

  return ensureCurrentTurn(state).items
}

/**
 * Reparents an existing tool call without replacing it, preserving references used to pair later completion events.
 */
function moveToolCallToTarget(state: ProcessingState, item: ToolCallItem, targetItems: TurnItem[]): void {
  const currentItems = state.toolCallContainers.get(item.toolCall.toolCallId)
  if (!currentItems || currentItems === targetItems) return

  const currentIndex = currentItems.indexOf(item)
  if (currentIndex === -1) return

  currentItems.splice(currentIndex, 1)
  targetItems.push(item)
  state.toolCallContainers.set(item.toolCall.toolCallId, targetItems)
}

function setUserMessageTurn(state: ProcessingState, source: string, subagentId: string | null, turn: Turn): void {
  const turnsBySubagent = state.userMessagesBySource.get(source) ?? new Map<string | null, Turn>()
  turnsBySubagent.set(subagentId, turn)
  state.userMessagesBySource.set(source, turnsBySubagent)
}

function processUserMessage(event: UserMessageEvent, state: ProcessingState): void {
  // Skip injected runtime messages (e.g. skill-*, instruction-discovery) —
  if (isInjectedSource(event.data.source)) return
  if ((event.data as Record<string, unknown>).isAutopilotContinuation) return

  const subagentId = resolveEventSubagentId(state, event)

  // Dedup: if a turn with the same source already exists (e.g. pending→real
  // transition), update it in place instead of creating a new turn.
  const source = event.data.source
  if (source) {
    const existingTurn = state.userMessagesBySource.get(source)?.get(subagentId)
    if (existingTurn) {
      existingTurn.userMessage = createUserMessageItem(event)
      if (!subagentId) {
        state.currentTurn = existingTurn
      }
      return
    }
  }

  if (subagentId) {
    const subagentItem = state.activeSubagents.get(subagentId)
    if (subagentItem) {
      const turn = createTurn(null)
      turn.userMessage = createUserMessageItem(event)
      subagentItem.group.turns.push(turn)
      if (source) {
        setUserMessageTurn(state, source, subagentId, turn)
      }
      return
    }
  }

  // Close current turn and start a new one
  if (state.currentTurn) {
    state.currentTurn.isComplete = true
  }

  // Reset intent — the previous turn's intent is stale
  state.currentIntent = null

  const turn = createTurn(null)
  turn.userMessage = createUserMessageItem(event)
  state.currentTurn = turn
  state.items.push(turn)

  if (source) {
    setUserMessageTurn(state, source, null, turn)
  }
}

function processAssistantTurnStart(event: AssistantTurnStartEvent, state: ProcessingState): void {
  const turn = ensureCurrentTurn(state)
  turn.turnId = event.data.turnId
  state.isWorking = true
}

function processAssistantMessage(event: AssistantMessageEvent, state: ProcessingState): void {
  const data = event.data

  const targetItems = resolveTargetTurnItems(state, event)
  const parentToolCallId = resolveEventSubagentId(state, event)

  // Append assistant text item if there's content or reasoning
  if (data.content || data.reasoningText) {
    const textItem: AssistantTextItem = {
      kind: 'assistant_text',
      id: `${event.id}-text`,
      timestamp: event.timestamp,
      messageId: data.messageId,
      content: data.content,
      reasoningText: data.reasoningText ?? null,
    }

    targetItems.push(textItem)
  }

  // Create items for each tool request
  if (data.toolRequests) {
    for (const req of data.toolRequests) {
      // Skip if already in index (dedup)
      if (state.toolCallIndex.has(req.toolCallId)) continue

      // Skip ask_user and exit_plan_mode — these are handled by their
      // standalone events (user_input.requested, exit_plan_mode.requested)
      // which have properly typed fields in the schema.
      if (req.name === 'ask_user' || req.name === 'exit_plan_mode') continue

      // Regular tool call — startEvent is null until tool.execution_start arrives
      const toolCall: PairedToolCall = {
        toolCallId: req.toolCallId,
        toolName: req.name,
        toolTitle: getToolTitle(req),
        arguments: req.arguments,
        startEvent: null,
        completeEvent: null,
        parentToolCallId,
      }

      const toolCallItem: ToolCallItem = {
        kind: 'tool_call',
        id: `tool-${req.toolCallId}`,
        timestamp: event.timestamp,
        toolCall,
      }
      targetItems.push(toolCallItem)
      state.toolCallIndex.set(req.toolCallId, toolCallItem)
      state.toolCallContainers.set(req.toolCallId, targetItems)
    }
  }
}

function processToolExecutionStart(event: ToolExecutionStartEvent, state: ProcessingState): void {
  const data = event.data

  // If already registered from assistant.message toolRequests, backfill the startEvent
  const existing = state.toolCallIndex.get(data.toolCallId)
  if (existing) {
    existing.toolCall.startEvent = event
    existing.toolCall.toolTitle = getToolTitle(data) ?? existing.toolCall.toolTitle
    const parentToolCallId = resolveEventSubagentId(state, event)
    if (parentToolCallId) {
      existing.toolCall.parentToolCallId = parentToolCallId
      moveToolCallToTarget(state, existing, resolveTargetTurnItems(state, event))
    }
    return
  }

  const targetItems = resolveTargetTurnItems(state, event)

  const toolCall: PairedToolCall = {
    toolCallId: data.toolCallId,
    toolName: data.toolName,
    toolTitle: getToolTitle(data),
    arguments: data.arguments,
    startEvent: event,
    completeEvent: null,
    parentToolCallId: resolveEventSubagentId(state, event),
  }

  const toolCallItem: ToolCallItem = {
    kind: 'tool_call',
    id: `tool-${data.toolCallId}`,
    timestamp: event.timestamp,
    toolCall,
  }

  targetItems.push(toolCallItem)
  state.toolCallIndex.set(data.toolCallId, toolCallItem)
  state.toolCallContainers.set(data.toolCallId, targetItems)
}

function processToolExecutionComplete(event: ToolExecutionCompleteEvent, state: ProcessingState): void {
  const existing = state.toolCallIndex.get(event.data.toolCallId)
  if (existing) {
    existing.toolCall.completeEvent = event
    existing.toolCall.toolTitle = getToolTitle(event.data) ?? existing.toolCall.toolTitle
  }
}

function processAssistantIntent(event: AssistantIntentEvent, state: ProcessingState): void {
  state.currentIntent = event.data.intent
  const turn = ensureCurrentTurn(state)
  turn.intent = event.data.intent
}

function processAssistantTurnEnd(_event: SessionEvent, state: ProcessingState): void {
  if (state.currentTurn) {
    state.currentTurn.isComplete = true
  }
  state.currentIntent = null
}

function processSessionIdle(_event: SessionEvent, state: ProcessingState): void {
  state.isWorking = false
}

function processAbort(event: AbortEvent, state: ProcessingState): void {
  state.isWorking = false
  const abortItem: AbortItem = {
    kind: 'abort',
    id: event.id,
    timestamp: event.timestamp,
    reason: event.data.reason,
  }

  state.items.push(abortItem)
}

function processSubagentStarted(event: SubagentStartedLikeEvent, state: ProcessingState): void {
  const data = event.data

  // Pull metadata from the originating `task` tool call's arguments
  // (e.g. description="Explore repo structure", prompt="Explore the top-level...")
  // instead of using the raw agentDescription which contains unresolved template placeholders.
  const taskItem = state.toolCallIndex.get(data.toolCallId)
  const taskMeta = getTaskMetadata(taskItem)

  const group: SubagentGroup = {
    agentId: data.toolCallId,
    agentName: data.agentName,
    agentDisplayName: data.agentDisplayName,
    description: taskMeta.description,
    prompt: taskMeta.prompt || undefined,
    startEvent: event,
    endEvent: null,
    turns: [],
    isPending: true,
  }

  const subagentItem: SubagentGroupItem = {
    kind: 'subagent_group',
    id: `subagent-${data.toolCallId}`,
    timestamp: event.timestamp,
    group,
  }

  ensureCurrentTurn(state).items.push(subagentItem)
  state.activeSubagents.set(data.toolCallId, subagentItem)
  const agentId = (event as {agentId?: string}).agentId
  if (agentId) {
    state.activeSubagents.set(agentId, subagentItem)
  }
}

function processSubagentCompleted(event: SubagentCompletedLikeEvent, state: ProcessingState): void {
  const subagentItem = state.activeSubagents.get(event.data.toolCallId)
  if (subagentItem) {
    subagentItem.group.endEvent = event
    subagentItem.group.isPending = false
  }
}

function processSubagentFailed(event: SubagentFailedLikeEvent, state: ProcessingState): void {
  const subagentItem = state.activeSubagents.get(event.data.toolCallId)
  if (subagentItem) {
    subagentItem.group.endEvent = event
    subagentItem.group.isPending = false
  }
}

function processUserInputRequested(event: UserInputRequestedEvent, state: ProcessingState): void {
  const data = event.data
  const key = data.toolCallId ?? data.requestId

  const askItem: AskUserItem = {
    kind: 'ask_user',
    id: `ask-${key}`,
    timestamp: event.timestamp,
    toolCallId: key,
    question: data.question,
    choices: data.choices ?? null,
    allowFreeform: data.allowFreeform ?? true,
    completedEvent: null,
  }

  const targetItems = resolveTargetTurnItems(state)
  targetItems.push(askItem)
  state.promptIndex.set(data.requestId, askItem)
  if (data.toolCallId) {
    state.promptIndex.set(data.toolCallId, askItem)
  }
  state.isWorking = false
}

function processUserInputCompleted(event: UserInputCompletedEvent, state: ProcessingState): void {
  const item = state.promptIndex.get(event.data.requestId)
  if (item && item.kind === 'ask_user') {
    item.completedEvent = event
  }
}

function processPermissionRequested(event: PermissionRequestedEvent, state: ProcessingState): void {
  const data = event.data
  const eventData = data as unknown as Record<string, unknown>
  const permReq = eventData.permissionRequest as Record<string, unknown> | undefined
  const toolCallId = (permReq && typeof permReq.toolCallId === 'string' ? permReq.toolCallId : null) ?? data.requestId

  const permItem: PermissionItem = {
    kind: 'permission',
    id: `perm-${data.requestId}`,
    timestamp: event.timestamp,
    requestId: data.requestId,
    toolCallId,
    requestEvent: event,
    completedEvent: null,
  }

  const targetItems = resolveTargetTurnItems(state)
  targetItems.push(permItem)
  state.promptIndex.set(data.requestId, permItem)
  if (toolCallId !== data.requestId) {
    state.promptIndex.set(toolCallId, permItem)
  }
  state.isWorking = false
}

function processPermissionCompleted(event: PermissionCompletedEvent, state: ProcessingState): void {
  const item = state.promptIndex.get(event.data.requestId)
  if (item && item.kind === 'permission') {
    item.completedEvent = event
  }
}

function processElicitationRequested(event: ElicitationRequestedEvent, state: ProcessingState): void {
  const data = event.data
  const elicitItem: ElicitationItem = {
    kind: 'elicitation',
    id: `elicit-${data.requestId}`,
    timestamp: event.timestamp,
    requestId: data.requestId,
    message: data.message,
    schema: data.requestedSchema,
    toolCallId: data.toolCallId,
    completedEvent: null,
  }

  const targetItems = resolveTargetTurnItems(state)
  targetItems.push(elicitItem)
  state.promptIndex.set(data.requestId, elicitItem)
  state.isWorking = false
}

function processElicitationCompleted(event: ElicitationCompletedEvent, state: ProcessingState): void {
  const item = state.promptIndex.get(event.data.requestId)
  if (item && item.kind === 'elicitation') {
    item.completedEvent = event
  }
}

function processExitPlanModeRequested(event: ExitPlanModeRequestedEvent, state: ProcessingState): void {
  const data = event.data

  const planItem: PlanApprovalItem = {
    kind: 'plan_approval',
    id: `plan-${data.requestId}`,
    timestamp: event.timestamp,
    requestId: data.requestId,
    toolCallId: data.toolCallId ?? null,
    summary: data.summary,
    planContent: data.planContent ?? null,
    actions: data.actions ?? null,
    recommendedAction: data.recommendedAction ?? null,
    completedEvent: null,
  }

  const targetItems = resolveTargetTurnItems(state)
  targetItems.push(planItem)
  state.promptIndex.set(data.requestId, planItem)
  if (data.toolCallId) {
    state.promptIndex.set(data.toolCallId, planItem)
  }
  state.isWorking = false
}

function processExitPlanModeCompleted(event: ExitPlanModeCompletedEvent, state: ProcessingState): void {
  const item = state.promptIndex.get(event.data.requestId)
  if (item && item.kind === 'plan_approval') {
    item.completedEvent = event
  }
}

function processSessionStart(event: SessionStartEvent, state: ProcessingState): void {
  if (event.data.selectedModel) {
    state.sessionModel = event.data.selectedModel
  }
}

function processSessionModelChange(event: SessionModelChangeEvent, state: ProcessingState): void {
  state.sessionModel = event.data.newModel
}

function processRemoteSteerableChanged(event: SessionRemoteSteerableChangedEvent, state: ProcessingState): void {
  const targetItems = resolveTargetTurnItems(state)
  const item: RemoteSteerableItem = {
    kind: 'remote_steerable',
    id: event.id,
    timestamp: event.timestamp,
    enabled: event.data.remoteSteerable,
  }
  targetItems.push(item)

  if (event.data.remoteSteerable) {
    state.isWorking = false
  }
}

/**
 * Checks whether a command's source already appears in the processed events.
 * Matches by both command.id and command.external_id (for CCA/sweagentd sessions).
 */
function isCommandMatchedInEvents(
  command: Command,
  userMessagesBySource: Map<string, Map<string | null, Turn>>,
): boolean {
  return (
    userMessagesBySource.get(`command-${command.id}`)?.has(null) === true ||
    (command.external_id != null && userMessagesBySource.get(`command-${command.external_id}`)?.has(null) === true)
  )
}

/**
 * Appends synthetic pending turns for commands not yet matched in the event stream.
 * Only "queued" and "in_progress" user_message commands produce synthetic turns.
 */
function appendSyntheticCommandTurns(state: ProcessingState, commands: Command[] | undefined): void {
  if (!commands) return

  for (const command of commands) {
    if (command.state !== 'queued' && command.state !== 'in_progress') continue
    if (command.type && command.type !== 'user_message') continue

    const matched = isCommandMatchedInEvents(command, state.userMessagesBySource)
    if (matched) continue

    const commandSource = `command-${command.id}`
    if (state.currentTurn) {
      state.currentTurn.isComplete = true
    }

    const syntheticTurn = createTurn(null)
    syntheticTurn.userMessage = {
      kind: 'user_message',
      id: `synthetic-${command.id}`,
      timestamp: command.created_at,
      content: commandDisplayContent(command),
      source: commandSource,
      isPending: true,
      isDismissed: false,
    }

    state.currentTurn = syntheticTurn
    state.items.push(syntheticTurn)
    setUserMessageTurn(state, commandSource, null, syntheticTurn)
  }
}

/**
 * Sets isWorking when the last turn has a pending user message with no assistant items yet.
 */
function computeWorkingState(state: ProcessingState): void {
  const lastItem = state.items[state.items.length - 1]
  if (lastItem && isTurn(lastItem) && lastItem.userMessage?.isPending && lastItem.items.length === 0) {
    state.isWorking = true
  }
}

interface PendingPromptResult {
  pendingPrompt: NormalizedPrompt | null
  allUnresolvedPrompts: NormalizedPrompt[]
  serverResolvedIds: Set<string>
}

/**
 * Walks all turns to find pending and resolved prompts.
 * Returns the first unresolved prompt, all unresolved prompts, and the set of resolved prompt keys.
 */
function computePendingPrompts(state: ProcessingState): PendingPromptResult {
  const serverResolvedIds = new Set<string>()
  let pendingPrompt: NormalizedPrompt | null = null
  const allUnresolvedPrompts: NormalizedPrompt[] = []

  for (const conversationItem of state.items) {
    if (!isTurn(conversationItem)) continue

    for (const item of conversationItem.items) {
      if (!isPromptItem(item)) continue

      const key = getPromptKey(item)

      if (item.completedEvent !== null) {
        serverResolvedIds.add(key)
      } else {
        const normalized = promptItemToNormalized(item)
        allUnresolvedPrompts.push(normalized)
        if (!pendingPrompt) {
          pendingPrompt = normalized
        }
      }
    }
  }

  return {pendingPrompt, allUnresolvedPrompts, serverResolvedIds}
}

export function buildConversationModel(events: SessionEvent[], commands?: Command[]): ConversationModel {
  const state: ProcessingState = {
    items: [],
    currentTurn: null,
    toolCallIndex: new Map(),
    toolCallContainers: new Map(),
    promptIndex: new Map(),
    activeSubagents: new Map(),
    userMessagesBySource: new Map(),
    currentIntent: null,
    isWorking: false,
    sessionModel: null,
  }

  for (const event of events) {
    try {
      switch (event.type) {
        case 'user.message':
          processUserMessage(event, state)
          break
        case 'assistant.turn_start':
          processAssistantTurnStart(event, state)
          break
        case 'assistant.message':
          processAssistantMessage(event, state)
          break
        case 'tool.execution_start':
          processToolExecutionStart(event, state)
          break
        case 'tool.execution_complete':
          processToolExecutionComplete(event, state)
          break
        case 'assistant.intent':
          processAssistantIntent(event, state)
          break
        case 'assistant.turn_end':
          processAssistantTurnEnd(event, state)
          break
        case 'session.idle':
          processSessionIdle(event, state)
          break
        case 'abort':
          processAbort(event, state)
          break
        case 'custom_agent.started':
        case 'subagent.started':
          processSubagentStarted(event, state)
          break
        case 'custom_agent.completed':
        case 'subagent.completed':
          processSubagentCompleted(event, state)
          break
        case 'custom_agent.failed':
        case 'subagent.failed':
          processSubagentFailed(event, state)
          break
        case 'session.start':
          processSessionStart(event, state)
          break
        case 'session.model_change':
          processSessionModelChange(event, state)
          break
        case 'session.remote_steerable_changed':
          processRemoteSteerableChanged(event, state)
          break
        case 'user_input.requested':
          processUserInputRequested(event, state)
          break
        case 'user_input.completed':
          processUserInputCompleted(event, state)
          break
        case 'permission.requested':
          processPermissionRequested(event, state)
          break
        case 'permission.completed':
          processPermissionCompleted(event, state)
          break
        case 'elicitation.requested':
          processElicitationRequested(event, state)
          break
        case 'elicitation.completed':
          processElicitationCompleted(event, state)
          break
        case 'exit_plan_mode.requested':
          processExitPlanModeRequested(event, state)
          break
        case 'exit_plan_mode.completed':
          processExitPlanModeCompleted(event, state)
          break
        default:
          break
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to process event ${event.id} (type=${event.type}):`, err)
    }
  }

  appendSyntheticCommandTurns(state, commands)
  computeWorkingState(state)
  const {pendingPrompt, allUnresolvedPrompts, serverResolvedIds} = computePendingPrompts(state)

  const hasVisibleContent = state.items.some(item =>
    isTurn(item)
      ? (item.userMessage !== null && isUserMessageVisible(item.userMessage)) || item.items.some(isTurnItemVisible)
      : true,
  )

  return {
    items: state.items,
    currentIntent: state.currentIntent,
    isWorking: state.isWorking,
    sessionModel: state.sessionModel,
    pendingPrompt,
    allUnresolvedPrompts,
    serverResolvedIds,
    hasVisibleContent,
    mcpToolNameMappings: extractMcpMappingsFromItems(state.items),
  }
}
